/**
 * apps/web/src/lib/format.ts
 *
 * Display formatters for the product UI. Money over the wire is always a DECIMAL
 * STRING (never a float) and dates are ISO-8601 strings, so these helpers parse
 * defensively and never throw on bad input (they fall back to a neutral dash).
 *
 * The active locale follows the UI language: `LanguageProvider` calls
 * `setFormatLocale(lang)` on boot and on every switch, so these pure-by-call
 * helpers render dates, money, and relative time in the chosen language without
 * each of the ~10 call sites having to thread a locale through. NOTE: per house
 * style we never use the em-dash character; the empty fallback is a plain hyphen.
 */

import type { Lang } from './strings';

const EMPTY = '-';

/** Intl locale tag per UI language. */
const LOCALE_TAG: Record<Lang, string> = { it: 'it-IT', en: 'en-US' };

/** Relative-time + date-range words per UI language. */
const WORDS: Record<Lang, {
  now: string;
  minAgo: (n: number) => string;
  hoursAgo: (n: number) => string;
  daysAgo: (n: number) => string;
  from: (d: string) => string;
  until: (d: string) => string;
}> = {
  it: {
    now: 'adesso',
    minAgo: (n) => `${n} min fa`,
    hoursAgo: (n) => `${n} h fa`,
    daysAgo: (n) => `${n} g fa`,
    from: (d) => `dal ${d}`,
    until: (d) => `fino al ${d}`,
  },
  en: {
    now: 'just now',
    minAgo: (n) => `${n} min ago`,
    hoursAgo: (n) => `${n} h ago`,
    daysAgo: (n) => `${n} d ago`,
    from: (d) => `from ${d}`,
    until: (d) => `until ${d}`,
  },
};

// Module-level active locale. Defaults to Italian (the product's primary
// locale); `setFormatLocale` is called by LanguageProvider before first paint.
let activeLang: Lang = 'it';
let activeLocale: string = LOCALE_TAG.it;

/** Point the formatters at a UI language. Called by LanguageProvider. */
export function setFormatLocale(lang: Lang): void {
  activeLang = lang;
  activeLocale = LOCALE_TAG[lang];
}

/**
 * Format a decimal-string money amount with its ISO currency code.
 * `'1234.5000'`, `'EUR'` -> `'1.234,50 €'`. Bad input falls back to the dash.
 */
export function formatMoney(amount: string | null | undefined, currency = 'EUR'): string {
  if (amount === null || amount === undefined || amount === '') return EMPTY;
  const value = Number(amount);
  if (!Number.isFinite(value)) return EMPTY;
  try {
    return new Intl.NumberFormat(activeLocale, {
      style: 'currency',
      currency,
      maximumFractionDigits: 2,
    }).format(value);
  } catch {
    // Unknown currency code: render the number with a trailing code.
    return `${value.toLocaleString(activeLocale, { maximumFractionDigits: 2 })} ${currency}`;
  }
}

/** Format an ISO date as `gg mmm aaaa` (e.g. `02 giu 2026`). */
export function formatDate(iso: string | null | undefined): string {
  if (!iso) return EMPTY;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return EMPTY;
  return new Intl.DateTimeFormat(activeLocale, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(date);
}

/** Format an ISO instant as date + time (`02 giu 2026, 14:30`). */
export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return EMPTY;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return EMPTY;
  return new Intl.DateTimeFormat(activeLocale, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

/** Short relative time: `2 min fa`, `3 h fa`, `5 g fa`, else a date. */
export function formatRelative(iso: string | null | undefined): string {
  if (!iso) return EMPTY;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return EMPTY;
  const words = WORDS[activeLang];
  const diffMs = Date.now() - date.getTime();
  const minutes = Math.round(diffMs / 60_000);
  if (minutes < 1) return words.now;
  if (minutes < 60) return words.minAgo(minutes);
  const hours = Math.round(minutes / 60);
  if (hours < 24) return words.hoursAgo(hours);
  const days = Math.round(hours / 24);
  if (days < 7) return words.daysAgo(days);
  return formatDate(iso);
}

/** Human file size from a byte count: `1,2 MB`. Null/0 falls back to the dash. */
export function formatBytes(bytes: number | null | undefined): string {
  if (bytes === null || bytes === undefined || bytes <= 0) return EMPTY;
  const units = ['B', 'KB', 'MB', 'GB'] as const;
  const exponent = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  const value = bytes / 1024 ** exponent;
  return `${value.toLocaleString(activeLocale, { maximumFractionDigits: 1 })} ${units[exponent]}`;
}

/** Round a 0..1 confidence to a whole-number percentage string: `0.873` -> `87%`. */
export function formatPercent(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return EMPTY;
  return `${Math.round(value * 100)}%`;
}

/**
 * Format a start/end date pair: `02 giu 2026 - 30 set 2026`. Omits a missing
 * end (`dal 02 giu 2026`) or start (`fino al 30 set 2026`); both empty -> dash.
 */
export function formatDateRange(
  start: string | null | undefined,
  end: string | null | undefined,
): string {
  if (!start && !end) return EMPTY;
  if (start && end) return `${formatDate(start)} - ${formatDate(end)}`;
  const words = WORDS[activeLang];
  if (start) return words.from(formatDate(start));
  return words.until(formatDate(end));
}
